import React, { useState } from 'react';
import { ChevronLeft, Info, Lock, MapPin, Clock, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Calendar } from '../components/Calendar';
import { TimePicker } from '../components/TimePicker';
import { DepositPolicyModal } from '../components/DepositPolicyModal';
import { PaymentInput } from '../components/PaymentInput';
import { Badge } from '../components/Badge';
import { BookingRequest } from '../App';
interface BookingFlowProps {
  itemId: string;
  onBack: () => void;
  onComplete: (booking: BookingRequest) => void;
}
export function BookingFlow({
  onBack,
  onComplete
}: BookingFlowProps) {
  const [step, setStep] = useState(1);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [pickupTime, setPickupTime] = useState('8:00 AM');
  const [dropoffTime, setDropoffTime] = useState('10:00 AM');
  const [ownerNote, setOwnerNote] = useState('');
  const [showPickupTimePicker, setShowPickupTimePicker] = useState(false);
  const [showDropoffTimePicker, setShowDropoffTimePicker] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  // Payment validation state
  const [isPaymentValid, setIsPaymentValid] = useState(false);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiry: '',
    cvv: '',
    cardholderName: ''
  });
  const mockItem = {
    title: 'Wireless Power Drill',
    image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400&q=80',
    owner: {
      name: 'Oliver M.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80'
    },
    pricing: {
      day: 15
    },
    deposit: 50
  };
  const calculateTotal = () => {
    const days = selectedDates.length === 2 ? Math.max(1, Math.ceil((selectedDates[1].getTime() - selectedDates[0].getTime()) / (1000 * 60 * 60 * 24))) : 1;
    const rentalAmount = mockItem.pricing.day * days;
    return {
      rental: rentalAmount,
      deposit: mockItem.deposit,
      total: rentalAmount
    };
  };
  const totals = calculateTotal();
  const canContinueFromDates = selectedDates.length === 2;
  const handleCompleteBooking = () => {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 72 * 60 * 60 * 1000); // 72 hours from now
    const booking: BookingRequest = {
      id: Date.now().toString(),
      itemTitle: mockItem.title,
      itemImage: mockItem.image,
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      pickupTime,
      dropoffTime,
      ownerNote,
      submittedAt: now,
      expiresAt,
      status: 'pending',
      location: '0.3 mi away',
      pricing: totals
    };
    onComplete(booking);
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric'
    });
  };
  const formatDateTime = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-592">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-sand/50" data-id="element-593">
        <div className="max-w-md mx-auto px-4 py-3" data-id="element-594">
          <div className="flex items-center justify-between mb-3" data-id="element-595">
            <button onClick={step === 1 ? onBack : step === 3 ? () => {} : () => setStep(step - 1)} className="p-2 -ml-2 hover:bg-sand/20 rounded-full transition-colors" data-id="element-596">
              <ChevronLeft size={24} className="text-charcoal" data-id="element-597" />
            </button>
            <h1 className="font-semibold text-charcoal" data-id="element-598">
              {step === 1 ? 'Select Dates to Book' : step === 2 ? 'Payment Method' : 'Confirmation'}
            </h1>
            <div className="w-10" data-id="element-599" />
          </div>

          {/* Progress Indicator */}
          {step < 3 && <div className="flex gap-2" data-id="element-600">
              <div className={`h-1 flex-1 rounded-full transition-colors ${step >= 1 ? 'bg-dark-green' : 'bg-sand'}`} data-id="element-601" />
              <div className={`h-1 flex-1 rounded-full transition-colors ${step >= 2 ? 'bg-dark-green' : 'bg-sand'}`} data-id="element-602" />
            </div>}
        </div>
      </div>

      <div className="max-w-md mx-auto overflow-hidden" data-id="element-603">
        <AnimatePresence mode="wait" custom={step} data-id="element-604">
          {step === 1 && <motion.div key="step1" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="px-4 py-6 space-y-5" data-id="element-605">
              {/* Item Summary Card */}
              <div className="flex gap-3 bg-white rounded-ios-lg p-4 shadow-sm border border-sand/50" data-id="element-606">
                <img src={mockItem.image} alt={mockItem.title} className="w-16 h-16 rounded-ios object-cover" data-id="element-607" />
                <div className="flex-1" data-id="element-608">
                  <h3 className="font-semibold text-charcoal" data-id="element-609">
                    {mockItem.title}
                  </h3>
                  <p className="text-sm text-moss-gray mt-1" data-id="element-610">
                    Select your rental period
                  </p>
                  <div className="mt-2" data-id="element-611">
                    <span className="text-lg font-bold text-dark-green" data-id="element-612">
                      ${mockItem.pricing.day}
                    </span>
                    <span className="text-xs text-moss-gray ml-1" data-id="element-613">per day</span>
                  </div>
                </div>
              </div>

              {/* Calendar */}
              <div data-id="element-614">
                <label className="block text-sm font-semibold text-charcoal mb-3" data-id="element-615">
                  Select Dates
                </label>
                <Calendar selectedDates={selectedDates} onDateSelect={setSelectedDates} mode="range" data-id="element-616" />
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-3" data-id="element-617">
                <div data-id="element-618">
                  <label className="block text-sm font-semibold text-charcoal mb-2" data-id="element-619">
                    Select Pick-up time
                  </label>
                  <button onClick={() => setShowPickupTimePicker(true)} className="w-full px-4 py-3 bg-white border-2 border-sand rounded-ios text-left text-charcoal font-medium hover:border-dark-green/30 transition-colors active:scale-[0.98]" data-id="element-620">
                    {pickupTime}
                  </button>
                </div>
                <div data-id="element-621">
                  <label className="block text-sm font-semibold text-charcoal mb-2" data-id="element-622">
                    Select Drop-Off time
                  </label>
                  <button onClick={() => setShowDropoffTimePicker(true)} className="w-full px-4 py-3 bg-white border-2 border-sand rounded-ios text-left text-charcoal font-medium hover:border-dark-green/30 transition-colors active:scale-[0.98]" data-id="element-623">
                    {dropoffTime}
                  </button>
                </div>
              </div>

              {/* Optional Note */}
              <div data-id="element-624">
                <label className="block text-sm font-semibold text-charcoal mb-2" data-id="element-625">
                  Add note for owner (optional)
                </label>
                <textarea value={ownerNote} onChange={e => setOwnerNote(e.target.value)} placeholder="Anything else the owner should know before reviewing your request?" rows={3} className="w-full px-4 py-3 bg-white border-2 border-sand rounded-ios text-charcoal placeholder-moss-gray focus:outline-none focus:border-dark-green resize-none" data-id="element-626" />
              </div>

              {/* Price Summary */}
              {canContinueFromDates && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white rounded-ios-lg p-4 shadow-sm border border-sand/50 space-y-2" data-id="element-627">
                  <div className="flex justify-between text-sm" data-id="element-628">
                    <span className="text-moss-gray" data-id="element-629">Rental amount</span>
                    <span className="font-medium text-charcoal" data-id="element-630">
                      ${totals.rental}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-sand/50" data-id="element-631">
                    <button onClick={() => setShowDepositModal(true)} className="flex items-center gap-1 text-moss-gray hover:text-dark-green transition-colors" data-id="element-632">
                      Deposit (Hold)
                      <Info size={14} className="text-dark-green" data-id="element-633" />
                    </button>
                    <span className="font-medium text-charcoal" data-id="element-634">
                      ${totals.deposit}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-sand/50" data-id="element-635">
                    <span className="font-bold text-charcoal" data-id="element-636">Total</span>
                    <span className="font-bold text-dark-green text-lg" data-id="element-637">
                      ${totals.total}
                    </span>
                  </div>
                </motion.div>}

              <Button variant="primary" fullWidth disabled={!canContinueFromDates} onClick={() => setStep(2)} className="h-12" data-id="element-638">
                Continue
              </Button>
            </motion.div>}

          {step === 2 && <motion.div key="step2" custom={2} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="px-4 py-6 space-y-5" data-id="element-639">
              {/* Security Info */}
              <div className="bg-sky-blue/10 border border-sky-blue/30 rounded-ios-lg p-4" data-id="element-640">
                <div className="flex items-start gap-3" data-id="element-641">
                  <Lock size={20} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-642" />
                  <div data-id="element-643">
                    <p className="text-sm text-charcoal leading-relaxed" data-id="element-644">
                      <strong data-id="element-645">Safe & Secure:</strong> Your deposit is held and
                      only released after successful return. Your card will not
                      be charged unless there's damage or late return.{' '}
                      <button onClick={() => setShowDepositModal(true)} className="text-dark-green font-medium hover:underline" data-id="element-646">
                        See full policy here.
                      </button>
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Form with Validation */}
              <PaymentInput onValidationChange={setIsPaymentValid} onCardDataChange={setPaymentData} data-id="element-647" />

              {/* Payment Summary */}
              <div className="bg-white rounded-ios-lg p-4 shadow-sm border border-sand/50 space-y-2" data-id="element-648">
                <div className="flex justify-between text-sm" data-id="element-649">
                  <span className="text-moss-gray" data-id="element-650">Rental amount</span>
                  <span className="font-medium text-charcoal" data-id="element-651">
                    ${totals.rental}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-sand/50" data-id="element-652">
                  <button onClick={() => setShowDepositModal(true)} className="flex items-center gap-1 text-moss-gray hover:text-dark-green transition-colors" data-id="element-653">
                    Deposit (Hold)
                    <Info size={14} className="text-dark-green" data-id="element-654" />
                  </button>
                  <span className="font-medium text-charcoal" data-id="element-655">
                    ${totals.deposit}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-sand/50" data-id="element-656">
                  <span className="font-bold text-charcoal" data-id="element-657">Total</span>
                  <span className="font-bold text-dark-green text-lg" data-id="element-658">
                    ${totals.total}
                  </span>
                </div>
              </div>

              {/* Security Message */}
              <div className="flex items-start gap-2 p-3 bg-sand/20 rounded-ios" data-id="element-659">
                <span className="text-lg" data-id="element-660">🔒</span>
                <p className="text-xs text-charcoal/80 leading-relaxed" data-id="element-661">
                  You won't be charged unless the owner accepts your request.
                </p>
              </div>

              <Button variant="primary" fullWidth disabled={!isPaymentValid} onClick={() => setStep(3)} className="h-12" data-id="element-662">
                Request Booking
              </Button>
            </motion.div>}

          {step === 3 && <motion.div key="step3" custom={3} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="px-4 py-6 space-y-6" data-id="element-663">
              {/* Success Icon */}
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="flex justify-center" data-id="element-664">
                <div className="w-24 h-24 bg-light-green rounded-full flex items-center justify-center" data-id="element-665">
                  <CheckCircle size={48} className="text-white" data-id="element-666" />
                </div>
              </motion.div>

              {/* Success Message */}
              <div className="text-center" data-id="element-667">
                <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-668">
                  Booking Requested!
                </h2>
                <p className="text-moss-gray" data-id="element-669">Waiting for owner approval</p>
              </div>

              {/* Pending Approval Card */}
              <div className="bg-white rounded-ios-lg p-4 shadow-sm border border-sand/50" data-id="element-670">
                <div className="flex items-center gap-3 mb-3" data-id="element-671">
                  <Badge variant="warning" size="md" className="bg-clay/20 text-clay border-clay/30" data-id="element-672">
                    Pending Approval
                  </Badge>
                  <img src={mockItem.owner.avatar} alt={mockItem.owner.name} className="w-10 h-10 rounded-full object-cover" data-id="element-673" />
                </div>
                <p className="text-sm text-charcoal leading-relaxed" data-id="element-674">
                  {mockItem.owner.name} will review your request. You'll receive
                  a notification once approved. Your request will expire after
                  72 hours.
                </p>
              </div>

              {/* Pickup Details */}
              <div className="bg-white rounded-ios-lg p-4 shadow-sm border border-sand/50 space-y-3" data-id="element-675">
                <h3 className="font-semibold text-charcoal" data-id="element-676">Pickup Details</h3>
                <div className="flex gap-3 text-sm" data-id="element-677">
                  <MapPin size={18} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-678" />
                  <div data-id="element-679">
                    <p className="font-medium text-charcoal" data-id="element-680">
                      Pick-up location
                    </p>
                    <p className="text-moss-gray" data-id="element-681">
                      Sort out details with owner once accepted
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 text-sm" data-id="element-682">
                  <Clock size={18} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-683" />
                  <div data-id="element-684">
                    <p className="font-medium text-charcoal" data-id="element-685">Pick-up time</p>
                    <p className="text-moss-gray" data-id="element-686">
                      {selectedDates[0] && formatDate(selectedDates[0])},{' '}
                      {pickupTime}
                    </p>
                  </div>
                </div>
              </div>

              <Button variant="primary" fullWidth onClick={handleCompleteBooking} className="h-12" data-id="element-687">
                View / Edit Request
              </Button>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Time Pickers */}
      <TimePicker isOpen={showPickupTimePicker} onClose={() => setShowPickupTimePicker(false)} onSelect={setPickupTime} initialTime={pickupTime} label="Select Pick-up time" data-id="element-688" />

      <TimePicker isOpen={showDropoffTimePicker} onClose={() => setShowDropoffTimePicker(false)} onSelect={setDropoffTime} initialTime={dropoffTime} label="Select Drop-Off time" data-id="element-689" />

      {/* Deposit Policy Modal */}
      <DepositPolicyModal isOpen={showDepositModal} onClose={() => setShowDepositModal(false)} depositAmount={mockItem.deposit} data-id="element-690" />
    </div>;
}